import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import clamp from 'lodash/clamp';
import { live } from 'lit/directives/live.js';
import { EVENT_INCREMENT, EVENT_DECREMENT, EVENTS_OPTIONS } from '@/components/counters/constants.js';
import styles from './counter.scss?inline';
import { getMessage } from '@/localization.js';
import messages from './localization.json';

export default class SotbitCounter extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        _displayValue: { state: true }, // final formatted value
        _lastValue: { state: true }, // temporary value before blur event
        _value: { state: true }, // temporary raw value
        value: { type: Number }, // final raw value
        disabled: { type: Boolean },
        minValue: { type: Number, attribute: 'min' },
        maxValue: { type: Number, attribute: 'max' },
        step: { type: Number },
        size: { type: String },
        name: { type: String },
        allowDecimal: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.step = 1;
        this.allowDecimal = false;
        this.minValue = null;
        this.maxValue = null;
        this._lastValue = null;
        this.name = null;
    }

    get value() {
        return this._value;
    }

    set value(value) {
        value = typeof value !== 'string' ? value?.toString() : value;
        value = value?.replace(',', '.') || '0';

        if (!this.isValidNumber(value)) {
            return;
        }

        const oldValue = this.value;
        this._value = this.getClosestInRange(value);
        let displayValue = this._value.toString();

        if (value.includes('.') && !displayValue.includes('.')) {
            displayValue = displayValue + value.substring(value.indexOf('.'));
        }

        this._displayValue = this.valueNumberFormat(displayValue);
        this.requestUpdate('data', oldValue);
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);

        if (_changedProperties.has('step') && _changedProperties.get('step') !== this.step) {
            this.allowDecimal = !Number.isInteger(Number(this.step));
        }
    }

    isValidNumber(value) {
        if (value === null) {
            return false;
        }

        return !Number.isNaN(Number(value));
    }

    getClosestInRange(value) {
        value = Number(value);
        const isMinValid = this.isValidNumber(this.minValue);
        const isMaxValid = this.isValidNumber(this.maxValue);

        if (!isMinValid && !isMaxValid) {
            return value;
        }

        if (isMinValid && isMaxValid) {
            return clamp(value, this.minValue, this.maxValue);
        }

        if (isMinValid) {
            return Math.max(value, this.minValue);
        }

        if (isMaxValid) {
            return Math.min(value, this.maxValue);
        }

        return value;
    }

    valueNumberFormat(value) {
        const valueString = typeof value !== 'string' ? value.toString() : value;
        let prepared = valueString.replace(/[^0-9\s,\.]/g, '');
        prepared = prepared.replace(/,/g, '.');
        const regexDig = /[1-9]/;

        if (valueString.length > 1 && valueString[0] === '0' && regexDig.test(valueString[1])) {
            prepared = prepared.slice(1);
        }

        const firstPointIndex = prepared.indexOf('.');

        if (firstPointIndex !== -1) {
            const integerPart = prepared.substring(0, firstPointIndex).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            const fractionalPart = prepared.substring(firstPointIndex + 1);
            const strippedFractionalPart = fractionalPart.replace(/\./g, '');
            prepared = integerPart + '.' + strippedFractionalPart;
        } else {
            prepared = prepared.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        }

        return prepared || '';
    }

    getDecimalPlaces() {
        const step = this.step.toString().replace(',', '.');

        if (!step.includes('.')) {
            return 0;
        }

        const parts = step.split('.');

        return parts[1] ? parts[1].length : 0;
    }

    _parseValue(str) {
        str = str?.toString()?.replace(',', '.') || '0';

        let numStr = str.replace(/[^\d.]/g, '');
        if (!numStr) {
            return 0;
        }

        if (numStr.startsWith('.')) {
            numStr = '0' + numStr;
        }

        const parts = numStr.split('.');

        if (parts.length > 1) {
            numStr = parts[0] + '.' + parts.slice(1).join('');
        }

        return numStr;
    }

    _handleInput({ target }) {
        this.value = this._parseValue(target.value);

        this.dispatchEvent(new Event('input', EVENTS_OPTIONS));
    }

    _handleFocus() {
        if (this.value === 0) {
            this._displayValue = '';
        }

        this._lastValue = this.value;
    }

    _handleBlur() {
        if (this._lastValue !== this.value) {
            // simulate change event
            this.dispatchEvent(new Event('change', EVENTS_OPTIONS));
        }

        this._lastValue = null;
        this._displayValue = this.valueNumberFormat(this.value);
    }

    _handleKeyUp(event) {
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.increment();
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.decrement();
        }
    }

    _handleKeyDown(event) {
        const allowedKeys = [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
            'Home',
            'End',
            'ArrowUp',
            'ArrowDown',
        ];
        const dotKeys = [',', '.'];
        const allowDot = dotKeys.includes(event.key) && this.allowDecimal && !this._displayValue.includes('.');

        if (!event.key.match(/^\d$/) && !allowedKeys.includes(event.key) && !allowDot) {
            event.preventDefault();
        }
    }

    increment() {
        if (this.disabled) {
            return;
        }

        this.value = Number(this.value + this.step)?.toFixed(this.getDecimalPlaces());
        this._lastValue = this.value;

        this.dispatchEvent(new Event(EVENT_INCREMENT, EVENTS_OPTIONS));
    }

    decrement() {
        if (this.disabled) {
            return;
        }

        this.value = Number(this.value - this.step)?.toFixed(this.getDecimalPlaces());
        this._lastValue = this.value;

        this.dispatchEvent(new Event(EVENT_DECREMENT, EVENTS_OPTIONS));
    }

    render() {
        return html` <div class="counter" part="counter-wrapper">
            <button
                class="counter-decrement"
                @click="${this.decrement}"
                ?disabled="${this.disabled || (this.minValue !== null && this.value <= this.minValue)}"
                part="decrement"
                aria-label="${getMessage(messages, 'decrement')}"
            >
                <div class="background"></div>
                <div class="outline"></div>
                <sotbit-icon part="minus" no-padding icon="minus" size="${this.size}"></sotbit-icon>
            </button>
            <div class="input-wrapper" part="counter-wrapper">
                <input
                    .value="${live(this._displayValue)}"
                    min="${this.minValue || nothing}"
                    max="${this.maxValue || nothing}"
                    @input="${this._handleInput}"
                    @keydown="${this._handleKeyDown}"
                    @keyup="${this._handleKeyUp}"
                    @focus="${this._handleFocus}"
                    @blur="${this._handleBlur}"
                    ?disabled="${this.disabled}"
                    type="text"
                    part="counter"
                    inputmode="numeric"
                    aria-label="${getMessage(messages, 'input')}"
                />
            </div>
            <button
                class="counter-increment"
                @click="${this.increment}"
                ?disabled="${this.disabled || (this.maxValue !== null && this.value >= this.maxValue)}"
                part="increment"
                aria-label="${getMessage(messages, 'increment')}"
            >
                <div class="background"></div>
                <div class="outline"></div>
                <sotbit-icon part="plus" no-padding icon="plus" size="${this.size}"></sotbit-icon>
            </button>
        </div>`;
    }
}
